// HAPPYCAP_RECURSOS_AZUL_FRESCO_V1
import { useEffect, useState } from "react";
import { getCentros } from "../services/api";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CentrosPage = () => {
  const { t } = useTranslation();
  const [originalCentros, setOriginalCentros] = useState([]);
  const [centros, setCentros] = useState([]);
  const [paises, setPaises] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [paisSeleccionado, setPaisSeleccionado] = useState("");
  const [ciudadSeleccionada, setCiudadSeleccionada] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCentros = async () => {
      setLoading(true);
      try {
        const res = await getCentros();
        setOriginalCentros(res.data);
        setCentros(res.data);

        const allPaises = [...new Set(res.data.map(c => c.pais).filter(Boolean))];
        const allCiudades = [...new Set(res.data.map(c => c.ciudad).filter(Boolean))];
        setPaises(allPaises);
        setCiudades(allCiudades);
        setErrorMsg("");
      } catch (error) {
        console.error("Error al cargar centros expertos", error);
        setErrorMsg(t("centers.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchCentros();
  }, []);

  useEffect(() => {
    const filtrados = originalCentros.filter((c) => {
      const matchPais = paisSeleccionado ? c.pais === paisSeleccionado : true;
      const matchCiudad = ciudadSeleccionada ? c.ciudad === ciudadSeleccionada : true;
      return matchPais && matchCiudad;
    });

    setCentros(filtrados);

    const ciudadesFiltradas = originalCentros
      .filter(c => (paisSeleccionado ? c.pais === paisSeleccionado : true))
      .map(c => c.ciudad)
      .filter(Boolean);

    setCiudades([...new Set(ciudadesFiltradas)]);
  }, [paisSeleccionado, ciudadSeleccionada, originalCentros]);

  const sortByTranslation = (arr) =>
    [...arr].sort((a, b) =>
      t(`countries.${a}`, a).localeCompare(t(`countries.${b}`, b), undefined, { sensitivity: "base" })
    );

  const sortStrings = (arr) =>
    [...arr].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

  return (
    <main className="max-w-5xl mx-auto p-6 mt-2 md:mt-5" aria-label={t("centers.title")}>
      <div className="flex flex-row items-center justify-between flex-wrap gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#247BA8] flex-1">
          {t("centers.title")}
        </h1>
        <button
          onClick={() => navigate("/recursos")}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#247BA8] text-white shadow-sm transition hover:bg-[#1B6388] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8]/30 focus-visible:ring-offset-2"
          aria-label={t("resources.view_resources")}
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      <fieldset className="mb-10 flex flex-wrap gap-4" aria-label={t("centers.filters")}>
        <legend className="sr-only">{t("centers.filters")}</legend>

        <div className="w-full min-w-0 sm:w-56">
          <select
            id="select-country"
            value={paisSeleccionado}
            onChange={(e) => {
              setPaisSeleccionado(e.target.value);
              setCiudadSeleccionada("");
            }}
            className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-700 shadow-sm outline-none transition hover:border-[#B8D8EA] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15 sm:text-[15px]"
          >
            <option value="">{t("centers.country", "País")}</option>
            {sortByTranslation(paises).map((p) => (
              <option key={p} value={p}>
                {t(`countries.${p}`, p)}
              </option>
            ))}
          </select>
        </div>

        {ciudades.length > 0 && (
          <div className="w-full min-w-0 sm:w-56">
            <select
              id="select-city"
              value={ciudadSeleccionada}
              onChange={(e) => setCiudadSeleccionada(e.target.value)}
              className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-700 shadow-sm outline-none transition hover:border-[#B8D8EA] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15 sm:text-[15px]"
            >
              <option value="">{t("centers.city", "Ciudad")}</option>
              {sortStrings(ciudades).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}
      </fieldset>

      {errorMsg && (
        <p className="text-red-600" role="alert" aria-live="assertive">
          {errorMsg}
        </p>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center mt-10 text-gray-600" role="status" aria-live="polite">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-3" />
          <p className="text-sm">{t("centers.loading")}</p>
        </div>
      ) : centros.length === 0 ? (
        null
      ) : (
        <section aria-label={t("centers.results")}>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {centros.map((c) => (
              <article
                key={c.id}
                className="bg-white shadow p-4 rounded-lg hover:shadow-md transition-shadow duration-200"
                aria-label={c.nombre}
              >
                <h2 className="font-semibold text-blue-700 mb-1">{c.nombre}</h2>
                <p>
                  <strong>{t("centers.country")}:</strong>{" "}
                  {t(`countries.${c.pais}`, c.pais)}
                </p>
                {c.ciudad && (
                  <p>
                    <strong>{t("centers.city")}:</strong> {c.ciudad}
                  </p>
                )}
                {/* HAPPYCAP_CENTROS_INGLES_V1 */}
                {Array.isArray(c.centros_ingles) &&
                  c.centros_ingles.length > 0 && (
                    <div className="mt-3">
                      <p className="font-semibold text-blue-700 mb-1">
                        {t("centers.specialist_centres", "Specialist centres")}:
                      </p>

                      <ul className="list-disc pl-5 space-y-1">
                        {c.centros_ingles.map((centro, index) => (
                          <li key={`${centro.enlace || "centro"}-${index}`}>
                            {centro.enlace ? (
                              <a
                                href={centro.enlace}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:underline"
                              >
                                {centro.nombre}
                              </a>
                            ) : (
                              <span>{centro.nombre}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {c.enlace && (
                  <a
                    href={c.enlace}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-blue-500 hover:underline"
                  >
                    {t("centers.more_info")}
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default CentrosPage;
